import React, { ReactNode } from 'react';
import { GiftingProduct, SelectedOption } from '../../product/@types';
import { OptionKeys, VariantsEntity } from '../../cart/@types';
import { GiftType } from './gifting';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedProduct: {
        product: GiftingProduct;
        index: number;
        type: GiftType;
        productIndex: number;
        shopCurrencyValue: string;
    } | null;
    selectedOptions: { [index: number]: SelectedOption };
    selectedComboProducts: { variantId: number; productId: number | undefined }[];
    swatchMap: any;
    handleSelectOption: (
        productIndex: number,
        index: number,
        optionName: OptionKeys,
        optionValue: string,
    ) => void;
    addToBox: (type: GiftType, p: GiftingProduct, index: number) => void;
    alertMessage: string | null;
}

const ProductOptionsModal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    selectedProduct,
    selectedOptions,
    selectedComboProducts,
    swatchMap,
    handleSelectOption,
    addToBox,
    alertMessage
}) => {
    if (!isOpen) return null;


    const isVariantOutOfStock = (variant: VariantsEntity) => {
        return variant.inventory_quantity === 0 && variant.inventory_policy === 'deny';
    };
    const getPriceOrRange = (selectedOptions: SelectedOption | null) => {
        const formatPrice = (price: number, currency: string) => {
            if (currency === 'Rp') {
                // currency Rp, remove 2 decimal places.
                return price.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                });
            } else {
                // Other currencies, format with 2 decimal places.
                return price.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            }
        };

        if (selectedProduct?.product?.variants?.length === 1) {
            const variant = selectedProduct?.product?.variants[0];
            const price = parseFloat(variant.price);
            if (!isNaN(price)) {
                return (
                    selectedProduct?.shopCurrencyValue +
                    ' ' +
                    formatPrice(price, selectedProduct?.shopCurrencyValue)
                );
            } else {
                return ''; // Handle invalid price
            }
        }

        const prices = (selectedProduct?.product?.variants || [])
            .filter((variant) => {
                return Object.keys(selectedOptions || {}).every((key) => {
                    return (
                        (selectedOptions || {})[key as OptionKeys] === variant[key as OptionKeys]
                    );
                });
            })
            .map((variant) => parseFloat(variant.price))
            .filter((price) => !isNaN(price)); // Filter out invalid prices

        if (prices.length === 0) {
            return ''; // Handle case where there are no valid prices
        }

        if (prices.every((price) => price === prices[0])) {
            return (
                selectedProduct?.shopCurrencyValue +
                ' ' +
                formatPrice(prices[0], selectedProduct?.shopCurrencyValue as any)
            );
        } else {
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            return `${selectedProduct?.shopCurrencyValue}${formatPrice(
                minPrice,
                selectedProduct?.shopCurrencyValue as any,
            )} - ${selectedProduct?.shopCurrencyValue}${formatPrice(
                maxPrice,
                selectedProduct?.shopCurrencyValue as any,
            )}`;
        }
    };
    const price = getPriceOrRange(selectedOptions[selectedProduct?.product?.id as any]);
    const isOutOfStock = selectedProduct?.product?.variants?.every(isVariantOutOfStock);

    let productImage;
    let productVariant = {} as VariantsEntity | undefined;
    if (selectedOptions[selectedProduct?.productIndex as any] !== undefined) {
        productVariant = selectedProduct?.product?.variants?.find(
            (variant) =>
                variant.option1 === selectedOptions[selectedProduct?.productIndex as any].option1,
        );
        productImage = selectedProduct?.product?.images?.find(
            (image) => image.id === productVariant?.image_id,
        );
    }
    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <button className="modal-close-button" onClick={onClose}>
                    &times;
                </button>
                <div className="modal-content">
                    <div className="product-image-in-modal-container">
                        <img
                            src={
                                productImage
                                    ? productImage?.src
                                    : selectedProduct?.product.image.src
                            }
                            alt={selectedProduct?.product.title}
                            className="product-image-in-modal"
                        />
                    </div>

                    <div className="product-item-body">
                        <p className="product-price">
                            {/* RM {p.variants?.[0].price} */}
                            {isOutOfStock
                                ? 'Out of Stock'
                                : `RM ${productVariant?.price !== undefined
                                    ? productVariant.price
                                    : price
                                }`}
                        </p>
                    </div>
                    <div className="product-image-in-modal-container-content">
                        <h5>{selectedProduct?.product.title}</h5>
                        {alertMessage && (
                            <p style={{ color: 'red', marginTop: '10px' }}>{alertMessage}</p>
                        )}
                        {selectedProduct?.product.options?.map((option, productOptionIndex) => {
                            if (selectedProduct?.product.variants?.length === 1) return null;
                            const optionValue =
                                selectedOptions[selectedProduct?.productIndex]?.[
                                `option${option.position}` as OptionKeys
                                ];

                            return (
                                <div className="rp-card-gifting-option" key={option.id}>
                                    <div>
                                        {/* display the product option eg, size/color */}
                                        <span className="rp-option-name">{option.name}</span>
                                        <span className="rp-option-value">
                                            {optionValue &&
                                                option.product_id === selectedProduct?.product.id
                                                ? `: ${optionValue}`
                                                : null}
                                        </span>
                                    </div>
                                    <div className="rp-card-gifting-option-values-container rp-card-option-values-container">
                                        {option.values?.map((value, optionIndex) => {
                                            // console.log({ value, optionValue });

                                            const possibleVariants = (
                                                selectedProduct?.product.variants || []
                                            ).filter((variant) => {
                                                // check if variant is available if selected
                                                const futureSelectedOptions = {
                                                    ...selectedOptions[
                                                    selectedProduct?.productIndex
                                                    ],
                                                    [`option${option.position}` as OptionKeys]:
                                                        value,
                                                };
                                                return Object.keys(futureSelectedOptions).every(
                                                    (key) => {
                                                        return (
                                                            futureSelectedOptions[
                                                            key as OptionKeys
                                                            ] === variant[key as OptionKeys]
                                                        );
                                                    },
                                                );
                                            });

                                            // function to check inventory for sale with considering selected combo products
                                            const inventoryForSale = (variant: VariantsEntity) => {
                                                const selectedVariants =
                                                    selectedComboProducts.filter(
                                                        (e) => e?.variantId === variant.id,
                                                    );
                                                if (variant.inventory_management === null)
                                                    return 999;
                                                if (variant.inventory_policy !== 'deny') return 999;
                                                return (
                                                    variant.inventory_quantity -
                                                    selectedVariants.length
                                                );
                                            };

                                            // check if all possible variants are out of stock
                                            const isDisabled = possibleVariants.every((variant) => {
                                                return (
                                                    inventoryForSale(variant as any) === 0 ||
                                                    inventoryForSale(variant as any) < 0
                                                );
                                            });

                                            return (
                                                <div
                                                    className={`rp-combo-option-item rp-option-item-combo${option.name === 'colour' ||
                                                            option.name === 'color' ||
                                                            option.name === 'Colour' ||
                                                            option.name === 'Color'
                                                            ? ' rp-option-colour-swatches'
                                                            : ''
                                                        }`}
                                                    key={value}
                                                    data-swatch={value}
                                                    style={
                                                        swatchMap[value]
                                                            ? {
                                                                backgroundColor: swatchMap[value],
                                                            }
                                                            : undefined
                                                    }
                                                >
                                                    <label>
                                                        {/* Radio button for color && size */}
                                                        <input
                                                            disabled={isDisabled}
                                                            type="radio"
                                                            id={
                                                                selectedProduct?.index +
                                                                selectedProduct?.product.id +
                                                                value
                                                            }
                                                            name={
                                                                selectedProduct?.index + option.name
                                                            }
                                                            value={value}
                                                            checked={optionValue === value}
                                                            onClick={(e) =>
                                                                handleSelectOption(
                                                                    selectedProduct?.productIndex,
                                                                    selectedProduct?.index,
                                                                    `option${option.position}` as OptionKeys,
                                                                    value,
                                                                )
                                                            }
                                                        />
                                                        {swatchMap[value] ? '' : value}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="product-image-in-modal-container">
                        <button
                            className="add-to-box-button"
                            style={{
                                border: '1.5px solid black',
                                color: 'black',
                                marginTop: '30px',
                            }}
                            onClick={() =>
                                addToBox(
                                    selectedProduct?.type as any,
                                    selectedProduct?.product as any,
                                    selectedProduct?.productIndex as any,
                                )
                            }
                        >
                            Add to Box
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductOptionsModal;
